import { useEffect, useRef } from 'react'
import Header from '../sections/Header'
import HeroSection from '../sections/HeroSection'
import ProblemSection from '../sections/ProblemSection'
import HowItWorksSection from '../sections/HowItWorksSection'
import FeaturesSection from '../sections/FeaturesSection'
import CoverageSection from '../sections/CoverageSection'
import PricingSection from '../sections/PricingSection'
import GuidesSection from '../sections/GuidesSection'
import PublicResourcesSection from '../sections/PublicResourcesSection'
import FinalCTA from '../sections/FinalCTA'
import Footer from '../sections/Footer'

export default function LandingPage() {
  const lenisRef = useRef<{ destroy: () => void } | null>(null)

  useEffect(() => {
    // Lenis smooth-scroll drives a perpetual gsap-ticker rAF loop. It is a
    // desktop-only enhancement: mobile and reduced-motion sessions never
    // download or start it, and it is lazy-loaded out of the initial bundle.
    if (window.innerWidth < 768) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return

    let cancelled = false
    let cleanup: (() => void) | undefined

    Promise.all([import('lenis'), import('gsap'), import('gsap/ScrollTrigger')])
      .then(([{ default: Lenis }, { gsap }, { ScrollTrigger }]) => {
        if (cancelled) return
        gsap.registerPlugin(ScrollTrigger)
        const lenis = new Lenis({ lerp: 0.12, smoothWheel: true })
        lenisRef.current = lenis
        lenis.on('scroll', ScrollTrigger.update)
        const raf = (time: number) => {
          lenis.raf(time * 1000)
        }
        gsap.ticker.add(raf)
        gsap.ticker.lagSmoothing(0)
        cleanup = () => {
          lenis.destroy()
          gsap.ticker.remove(raf)
        }
      })
      .catch(() => {
        // Smooth scrolling is decorative; native scrolling remains.
      })

    return () => {
      cancelled = true
      cleanup?.()
      lenisRef.current = null
    }
  }, [])

  return (
    <div className="relative">
      <Header />
      <main>
        <HeroSection />
        <ProblemSection />
        <HowItWorksSection />
        <FeaturesSection />
        <CoverageSection />
        <GuidesSection />
        <PublicResourcesSection />
        <PricingSection />

        {/* Social proof line replacing testimonials */}
        <section className="relative py-16 bg-[#0d1117]">
          <div className="max-w-[1280px] mx-auto px-6 text-center">
            <p className="text-[clamp(16px,1.5vw,20px)] text-[#9ca3af] italic">
              Built for electricians, plumbers, HVAC techs, general contractors, and every trade in between.
            </p>
          </div>
        </section>

        <FinalCTA />
      </main>
      <Footer />
    </div>
  )
}
